import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cancelProject, getActiveProject, getDashboard, getMe } from '../api';
import type { BodyfatTrendPoint, DashboardResponse, Goal, Project, UnitPreference, User } from '../types';
import { formatWeight } from '../units';
import { Spinner } from '../components/Spinner';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRate(weeklyKg: number | null, pref: UnitPreference): string {
  if (weeklyKg === null) return '—';
  if (pref === 'imperial') {
    const lb = weeklyKg / 0.45359237;
    return `${lb >= 0 ? '+' : ''}${lb.toFixed(2)} lb/wk`;
  }
  return `${weeklyKg >= 0 ? '+' : ''}${weeklyKg.toFixed(2)} kg/wk`;
}

// Pick the bodyfat_trend point closest to a target ISO date — used to find a
// ~30d-prior reference point for the "delta in 30d" line on the COMPOSITION
// tile. bodyfat_trend now arrives pre-fetched on the GET /dashboard payload
// (W.7 fold), already unscoped + 30-day-windowed server-side (dashboard.ts,
// mirroring coach.ts's identical query) — this is just the same nearest-date
// pick coach-shared.ts's bodyCompositionSection does with that data, kept
// local since it's presentation-only math over data that's already fetched.
function closestByDate(rows: BodyfatTrendPoint[], targetIso: string): BodyfatTrendPoint | null {
  if (rows.length === 0) return null;
  const target = new Date(targetIso + 'T00:00:00Z').getTime();
  let best: BodyfatTrendPoint | null = null;
  let bestDelta = Infinity;
  for (const r of rows) {
    const t = new Date(r.taken_at + 'T00:00:00Z').getTime();
    if (Number.isNaN(t)) continue;
    const delta = Math.abs(t - target);
    if (delta < bestDelta) {
      best = r;
      bestDelta = delta;
    }
  }
  return best;
}

// project.goal → verb phrase for the "Hey, let's <X>" greeting. Mirrors the
// verb-phrase shape of profile.primary_goal so the greeting reads naturally
// regardless of which fallback fires. Profile-overall fallback uses .replace
// passthrough (covers improve_fitness/athletic_performance per d662243).
const PROJECT_GOAL_VERB: Record<Goal, string> = {
  fat_loss: 'lose fat',
  recomp: 'recomp',
  muscle_gain: 'build muscle',
  maintenance: 'maintain',
};

export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    Promise.all([
      getDashboard(),
      // Active project drives the cancel affordance and the no-project CTA.
      // Failure here is non-fatal — fall back to no-project state and let the
      // user start a new one rather than blocking the dashboard.
      getActiveProject().catch(() => ({ project: null as Project | null })),
      // User row drives the maintenance/coast banner (slice C). Non-fatal —
      // if getMe fails we just don't render a banner.
      getMe().catch(() => null),
    ])
      .then(([d, p, u]) => {
        setData(d);
        setProject(p.project);
        setUser(u);
        // S8: if we arrived from POST /projects 409 the wizard hands the
        // blocking project's id via router state. Auto-open the cancel-
        // confirm modal scoped to that project, then clear the state so a
        // refresh doesn't re-open it. Mismatched / stale id (user cancelled
        // in another tab between 409 and arrival) → no-op silently.
        const navState = location.state as { autoConfirmCancelFor?: string } | null;
        if (navState?.autoConfirmCancelFor) {
          if (p.project && p.project.id === navState.autoConfirmCancelFor) {
            setConfirming(true);
          }
          navigate(location.pathname, { replace: true });
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
    // Mount-only consumption of location.state — re-running on location/
    // navigate changes would loop after we replace the state ourselves.
  }, []);

  const onConfirmCancel = async (): Promise<void> => {
    if (!project) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelProject(project.id);
      // Drop the project locally; the dashboard re-renders into no-active
      // state which surfaces the Start-a-project CTA. No need to refetch
      // /dashboard — none of its fields depend on the active project.
      setProject(null);
      setConfirming(false);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel project');
    } finally {
      setCancelling(false);
    }
  };

  if (error) return <div className="shell"><div className="errorbar">{error}</div></div>;
  if (!data) return <div className="center"><Spinner /></div>;

  const a = data.analytics;
  const pref: UnitPreference = data.profile?.unit_preference ?? 'metric';
  const noData = a.data_points === 0;
  const usingProfileWeight = a.current_weight_source === 'profile';

  // BODY COMPOSITION tile state. bodyfat_trend comes back most-recent-first
  // (dashboard.ts orders by date descending), already unscoped + 30-day
  // windowed — no client-side sort needed.
  const bodyfatTrend = data.bodyfat_trend;
  const latestBf = bodyfatTrend[0] ?? null;
  let bfDelta30: number | null = null;
  if (latestBf && bodyfatTrend.length > 1) {
    const targetIso = (() => {
      const d = new Date(latestBf.taken_at + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() - 30);
      return d.toISOString().slice(0, 10);
    })();
    const ref = closestByDate(bodyfatTrend.slice(1), targetIso);
    if (ref) bfDelta30 = +(latestBf.pct - ref.pct).toFixed(1);
  }

  // Maintenance / coast banner (slice C). DB CHECK constraint
  // users_maintenance_xor_coast guarantees mutex; the maintenance-first
  // render order here is defensive against a partial-write window in the
  // worker. Banner surfaces regardless of project state — a user can be
  // in maintenance/coast with or without an active project.
  const today = new Date().toISOString().slice(0, 10);
  const maintenanceUntil = user && user.maintenance_until && user.maintenance_until > today
    ? user.maintenance_until
    : null;
  const coastUntil = user && user.coast_until && user.coast_until > today
    ? user.coast_until
    : null;
  const modeBanner: { eyebrow: string; title: string } | null = (() => {
    if (maintenanceUntil) {
      const days = Math.max(0, Math.ceil((new Date(maintenanceUntil).getTime() - Date.now()) / 86_400_000));
      return {
        eyebrow: 'Locked in',
        title: `Maintenance mode — ${days} day${days === 1 ? '' : 's'} left`,
      };
    }
    if (coastUntil) {
      const days = Math.max(0, Math.ceil((new Date(coastUntil).getTime() - Date.now()) / 86_400_000));
      return {
        eyebrow: 'On a breather',
        title: `Coasting — ${days} day${days === 1 ? '' : 's'} left`,
      };
    }
    return null;
  })();

  // Caption beneath the hero weight. Three states:
  //  - profile fallback: show the user we have their starting number, nudge to log
  //  - have a projection: show the goal date
  //  - have data but no projection yet: keep going copy
  //  - nothing at all: log-first nudge (existing copy)
  const caption = usingProfileWeight
    ? "From your profile — log a check-in to start tracking the line."
    : noData
      ? "Log your first check-in and we'll start drawing the line."
      : a.predicted_goal_date
        ? `On track to hit your goal by ${fmtDate(a.predicted_goal_date)}.`
        : "Keep going — once we have a clear trend we'll project your goal date.";

  // Greeting verb: prefer active project goal (S13), fall back to profile-
  // overall primary_goal. Project-active users get project-shape vocab; users
  // with no active project see their profile-overall stance.
  const greetingVerb: string | null = project
    ? PROJECT_GOAL_VERB[project.goal]
    : data.profile?.primary_goal
      ? data.profile.primary_goal.replace(/_/g, ' ')
      : null;

  return (
    <div className="shell">
      <header className="row row--between rise" style={{ marginBottom: 8 }}>
        <div>
          <p className="eyebrow">Today</p>
          <h1 className="h1">Hey{greetingVerb ? `, let's ${greetingVerb}` : ''}</h1>
        </div>
      </header>

      <section className="section rise">
        <div className="hero">
          <p className="eyebrow">Current weight</p>
          <div>
            <span className="hero__big">
              {a.current_weight_kg === null
                ? '—'
                : pref === 'imperial'
                ? (a.current_weight_kg / 0.45359237).toFixed(1)
                : a.current_weight_kg.toFixed(1)}
            </span>
            <span className="hero__unit">{pref === 'imperial' ? 'lb' : 'kg'}</span>
          </div>
          <p className="hero__caption">{caption}</p>
        </div>
      </section>

      {modeBanner ? (
        <section className="section rise">
          <div className="card">
            <p className="eyebrow">{modeBanner.eyebrow}</p>
            <p className="h3" style={{ marginTop: 4 }}>{modeBanner.title}</p>
          </div>
        </section>
      ) : null}

      {!project ? (
        <section className="section rise">
          <button
            type="button"
            className="card card--interactive"
            onClick={() => navigate('/projects/new')}
            style={{ textAlign: 'left', width: '100%' }}
          >
            <p className="eyebrow">Next step</p>
            <p className="h3" style={{ marginTop: 4 }}>Start a project</p>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
              Set a goal, get a coaching plan, and start making progress.
            </p>
          </button>
        </section>
      ) : null}

      <section className="section rise">
        <h2 className="h2">Vitals</h2>
        <div className="stat-grid">
          <div className="stat">
            <div className="stat__label">Weekly rate</div>
            <div className="stat__value">{formatRate(a.weekly_rate_kg, pref)}</div>
            <div className="stat__delta">
              {a.weight_change_kg_30d === null
                ? 'Need more data'
                : `${a.weight_change_kg_30d >= 0 ? '+' : ''}${formatWeight(Math.abs(a.weight_change_kg_30d), pref)} 30d`}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">Plateau risk</div>
            <div className="stat__value">{Math.round(a.plateau_risk * 100)}%</div>
            <div className="stat__delta">
              {a.plateau ? <span className="tag tag--alert">Plateau detected</span> : <span className="tag tag--good">Moving</span>}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">Adherence (14d)</div>
            <div className="stat__value">{a.adherence_14d === null ? '—' : `${Math.round(a.adherence_14d * 100)}%`}</div>
            <div className="stat__delta">workouts completed</div>
          </div>
          <div className="stat">
            <div className="stat__label">Goal date</div>
            <div className="stat__value" style={{ fontSize: 18 }}>{fmtDate(a.predicted_goal_date)}</div>
            <div className="stat__delta">{a.days_to_goal === null ? '—' : `${a.days_to_goal} days to go`}</div>
          </div>
          <button
            type="button"
            className="stat stat--interactive"
            onClick={() => navigate('/checkin')}
            aria-label="Body composition"
          >
            <div className="stat__label">Composition</div>
            <div className="stat__value">{latestBf ? `${Math.round(latestBf.pct)}%` : '—'}</div>
            <div className="stat__delta">
              {latestBf
                ? bfDelta30 !== null
                  ? `${bfDelta30 >= 0 ? '+' : ''}${bfDelta30.toFixed(1)}% in 30d · user-reported`
                  : 'user-reported'
                : 'Log a check-in to start tracking'}
            </div>
          </button>
        </div>
      </section>

      {data.memory?.last_focus ? (
        <section className="section rise">
          <h2 className="h2">Coach focus</h2>
          <div className="card">
            <p className="eyebrow">Right now</p>
            <p className="h3" style={{ marginTop: 4 }}>{data.memory.last_focus}</p>
            {data.memory.summary ? (
              <p className="muted" style={{ marginTop: 8, lineHeight: 1.55 }}>{data.memory.summary}</p>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="section rise">
        <button
          type="button"
          className="card card--interactive"
          onClick={() => navigate('/checkin')}
          style={{ textAlign: 'left', width: '100%' }}
        >
          <p className="eyebrow">Daily</p>
          <p className="h3" style={{ marginTop: 4 }}>Log a check-in</p>
          <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>Weight, training, mood — get instant coach feedback.</p>
        </button>
      </section>

      {project ? (
        <section className="section rise">
          <button
            type="button"
            className="btn btn--primary btn--full"
            onClick={() => navigate(`/projects/${project.id}/workout-chat`)}
          >
            Talk to workout coach
          </button>
          <button
            type="button"
            className="btn btn--full"
            style={{ marginTop: 8 }}
            onClick={() => navigate(`/projects/${project.id}/complete`)}
          >
            Mark project complete
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--full"
            style={{ color: 'var(--danger)', marginTop: 8 }}
            onClick={() => {
              setCancelError(null);
              setConfirming(true);
            }}
          >
            Cancel project
          </button>
        </section>
      ) : null}

      {confirming && project ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 50,
          }}
        >
          <div className="card card--elevated" style={{ maxWidth: 380 }}>
            <p className="h3">Cancel this project?</p>
            <p className="muted" style={{ marginTop: 8, lineHeight: 1.55, fontSize: 14 }}>
              You'll lose your current goal and coaching plan. You can start a new
              project anytime.
            </p>
            {cancelError ? (
              <div className="errorbar" style={{ marginTop: 12 }}>{cancelError}</div>
            ) : null}
            <div className="row" style={{ marginTop: 16, gap: 8 }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirming(false)}
                disabled={cancelling}
              >
                Keep project
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  background: 'var(--danger)',
                  color: '#1f0707',
                  borderColor: 'transparent',
                  flex: 1,
                }}
                onClick={() => void onConfirmCancel()}
                disabled={cancelling}
              >
                {cancelling ? <><Spinner /> Cancelling…</> : 'Cancel project'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
